import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

export interface VendorDocument {
  id: string;
  vendor_id: string;
  company_id: string;
  document_type: string;
  file_name: string;
  file_path: string;
  file_size: number | null;
  expiry_date: string | null;
  notes: string | null;
  uploaded_by: string | null;
  created_at: string;
}

export const useVendorDocuments = (vendorId?: string) => {
  const { user } = useAuth();
  return useQuery({
    queryKey: ["vendor_documents", vendorId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("vendor_documents")
        .select("*")
        .eq("vendor_id", vendorId!)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return (data ?? []) as unknown as VendorDocument[];
    },
    enabled: !!user && !!vendorId,
  });
};

export const useUploadVendorDocument = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      vendor_id: string;
      document_type: string;
      file: File;
      expiry_date?: string;
      notes?: string;
    }) => {
      const { data: companyId, error: cidErr } = await supabase.rpc("my_company_id");
      if (cidErr) throw cidErr;
      const userId = (await supabase.auth.getUser()).data.user?.id;

      const filePath = `${companyId}/${input.vendor_id}/${Date.now()}_${input.file.name}`;
      const { error: uploadErr } = await supabase.storage
        .from("vendor-docs")
        .upload(filePath, input.file);
      if (uploadErr) throw uploadErr;

      const { error } = await supabase
        .from("vendor_documents")
        .insert({
          vendor_id: input.vendor_id,
          company_id: companyId,
          document_type: input.document_type,
          file_name: input.file.name,
          file_path: filePath,
          file_size: input.file.size,
          expiry_date: input.expiry_date || null,
          notes: input.notes || null,
          uploaded_by: userId,
        } as any);
      if (error) throw error;
    },
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ["vendor_documents", vars.vendor_id] });
    },
  });
};

export const useDeleteVendorDocument = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (doc: VendorDocument) => {
      await supabase.storage.from("vendor-docs").remove([doc.file_path]);
      const { error } = await supabase.from("vendor_documents").delete().eq("id", doc.id);
      if (error) throw error;
    },
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ["vendor_documents", vars.vendor_id] });
    },
  });
};
